(() => {
  if (window.fv2EditorialNewsTeaserInitialized) return;
  window.fv2EditorialNewsTeaserInitialized = true;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function splitToCharacters(target) {
    if (!target || target.dataset.charsReady === 'true') return;

    const rawText = (target.textContent || '').trim();
    if (!rawText) return;

    target.dataset.charsReady = 'true';
    target.setAttribute('aria-label', rawText);

    const fragment = document.createDocumentFragment();
    [...rawText].forEach((character, index) => {
      const charSpan = document.createElement('span');
      charSpan.className = 'fv2-char';
      charSpan.style.setProperty('--char-index', String(index));
      charSpan.textContent = character === ' ' ? '\u00A0' : character;
      fragment.appendChild(charSpan);
    });

    target.textContent = '';
    target.appendChild(fragment);
  }

  function makeSectionVisible(section) {
    if (!section) return;

    section
      .querySelectorAll('[data-editorial-char-reveal]')
      .forEach((node) => splitToCharacters(node));

    if (prefersReducedMotion) {
      section.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.classList.add('is-visible');
          } else {
            section.classList.remove('is-visible');
          }
        });
      },
      {
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.2,
      }
    );

    observer.observe(section);
  }

  function initCarousel(section) {
    if (!section || section.dataset.carouselReady === 'true') return;

    const carousel = section.querySelector('[data-editorial-carousel]');
    const track = section.querySelector('[data-editorial-track]');
    const slides = Array.from(section.querySelectorAll('[data-editorial-slide]'));

    if (!carousel || !track || slides.length === 0) return;

    section.dataset.carouselReady = 'true';

    let index = 0;
    const autoplayEnabled = carousel.dataset.editorialAutoplay === 'true' && !prefersReducedMotion;
    const autoplaySeconds = Number(carousel.dataset.editorialAutoplaySeconds || 6);
    const autoplayMs = Math.max(4, autoplaySeconds) * 1000;
    let autoPlayTimeoutId = null;
    let autoplayToken = 0;

    carousel.style.setProperty('--editorial-autoplay-duration', `${Math.max(4, autoplaySeconds)}s`);

    const dots = Array.from(section.querySelectorAll('[data-editorial-dot]'));
    const prevButton = section.querySelector('[data-editorial-prev]');
    const nextButton = section.querySelector('[data-editorial-next]');
    const viewport = section.querySelector('.fv2-editorial__viewport');
    let exitingSlideTimeoutId = null;
    let swipeState = null;

    function resetDotProgress(activeDot) {
      dots.forEach((dot) => {
        dot.classList.remove('is-progress');
      });

      if (!activeDot || !autoplayEnabled || slides.length < 2) return;

      // Force reflow so CSS animation reliably restarts each slide switch.
      void activeDot.offsetWidth;
      activeDot.classList.add('is-progress');
    }

    function clearAutoplayFallback() {
      window.clearTimeout(autoPlayTimeoutId);
      autoPlayTimeoutId = null;
    }

    function bindAutoplayToActiveDot(activeDot) {
      if (!autoplayEnabled || slides.length < 2 || !activeDot) return;

      autoplayToken += 1;
      const token = autoplayToken;
      const progressNode = activeDot.querySelector('.fv2-editorial__dot-progress');

      if (progressNode) {
        progressNode.addEventListener(
          'animationend',
          (event) => {
            if (event.animationName !== 'editorialDonutProgress') return;
            if (token !== autoplayToken) return;
            goTo(index + 1);
          },
          { once: true }
        );
      }

      // Fallback in case animationend is skipped by the browser/runtime.
      clearAutoplayFallback();
      autoPlayTimeoutId = window.setTimeout(() => {
        if (token !== autoplayToken) return;
        goTo(index + 1);
      }, autoplayMs + 150);
    }

    function updateCarousel(previousIndex = null) {
      window.clearTimeout(exitingSlideTimeoutId);

      slides.forEach((slide, slideIndex) => {
        slide.classList.remove('is-exiting');
        slide.classList.toggle('is-active', slideIndex === index);
        slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
      });

      if (previousIndex !== null && slides[previousIndex] && previousIndex !== index) {
        const previousSlide = slides[previousIndex];
        previousSlide.classList.add('is-exiting');
        exitingSlideTimeoutId = window.setTimeout(() => {
          previousSlide.classList.remove('is-exiting');
        }, 760);
      }

      let activeDot = null;
      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) activeDot = dot;
      });

      resetDotProgress(activeDot);
      bindAutoplayToActiveDot(activeDot);
    }

    function goTo(nextIndex) {
      const length = slides.length;
      const previousIndex = index;
      index = (nextIndex + length) % length;
      updateCarousel(previousIndex);
    }

    function onPointerDown(event) {
      if (!viewport || slides.length < 2) return;
      if (event.pointerType === 'mouse') return;

      swipeState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        deltaY: 0,
      };
    }

    function onPointerMove(event) {
      if (!swipeState || swipeState.pointerId !== event.pointerId) return;

      swipeState.deltaX = event.clientX - swipeState.startX;
      swipeState.deltaY = event.clientY - swipeState.startY;
    }

    function onPointerUp(event) {
      if (!swipeState || swipeState.pointerId !== event.pointerId) return;

      const { deltaX, deltaY } = swipeState;
      swipeState = null;

      if (Math.abs(deltaX) < 36) return;
      if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

      if (deltaX < 0) {
        goTo(index + 1);
      } else {
        goTo(index - 1);
      }
    }

    if (prevButton) {
      prevButton.addEventListener('click', () => goTo(index - 1));
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => goTo(index + 1));
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const dotIndex = Number(dot.dataset.editorialIndex || 0);
        goTo(dotIndex);
      });
    });

    if (viewport && slides.length > 1) {
      viewport.addEventListener('pointerdown', onPointerDown);
      viewport.addEventListener('pointermove', onPointerMove);
      viewport.addEventListener('pointerup', onPointerUp);
      viewport.addEventListener('pointercancel', onPointerUp);
      viewport.addEventListener('pointerleave', onPointerUp);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        autoplayToken += 1;
        clearAutoplayFallback();
        dots.forEach((dot) => dot.classList.remove('is-progress'));
      } else {
        updateCarousel();
      }
    });

    updateCarousel();
  }

  function initEditorialSections(root = document) {
    root.querySelectorAll('[data-fv2-editorial]').forEach((section) => {
      makeSectionVisible(section);
      initCarousel(section);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initEditorialSections());
  document.addEventListener('shopify:section:load', (event) => initEditorialSections(event.target));
})();
